import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Box, Stack } from "@mui/system";
import { useGet } from "../../actprep-ui/hooks/api";
import { apiUrls } from "../../actprep-ui/utils/urls";
import ScheduleItemList from "../../actprep-ui/components/ScheduleItemList";
import EmployeeImpersonationSelect from "../../actprep-ui/components/EmployeeImpersonationSelect";
import {
  Divider,
  Typography,
  Badge,
  IconButton,
  Chip,
  Skeleton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  CalendarIcon,
  MobileDatePicker,
  PickersDay,
} from "@mui/x-date-pickers-pro";
import { sum } from "../../actprep-ui/utils/helpers";
import { ArrowLeft, ArrowRight } from "@mui/icons-material";
import { scheduleItemTypes } from "../../actprep-ui/constants/program";
import { fullName } from "../../actprep-ui/utils/formatting";

export default function AdminProgramSchedule({
  impersonation: externalImpersonation,
  onImpersonationChange: externalOnImpersonationChange,
}) {
  const [date, setDate] = useState(dayjs());
  const [currentMonth, setCurrentMonth] = useState(date.startOf("month"));
  const [internalImpersonation, setInternalImpersonation] = useState(null);
  const impersonation =
    externalImpersonation !== undefined
      ? externalImpersonation
      : internalImpersonation;
  const setImpersonation =
    externalOnImpersonationChange ?? setInternalImpersonation;
  const impersonatedProfileUuid = impersonation?.profileUuid ?? null;

  // Fetch data with loading state for schedule (userProfileId before date when impersonating)
  const { data: scheduleData, isLoading: loadingSchedule } = useGet(
    apiUrls.admin.buildSchedule(
      date.format("YYYY-MM-DD"),
      impersonatedProfileUuid,
    ),
  );

  const { data: completedItemsData } = useGet(
    apiUrls.scheduling.scheduleItemCompletion(impersonatedProfileUuid),
    {
      params: { search_params: { date: date.format("YYYY-MM-DD") } },
      staleTime: 0,
    },
  );
  const { data: scheduleStatusData, refetch: refetchScheduleStatus } = useGet(
    apiUrls.admin.scheduleStatus(impersonatedProfileUuid),
    { staleTime: 0 },
  );

  // Memoized maps and totals
  const completedItemsMap = useMemo(() => {
    if (!completedItemsData) return {};
    return Object.fromEntries(
      completedItemsData.data.map(({ schedule_item, created_at }) => [
        schedule_item,
        created_at,
      ]),
    );
  }, [completedItemsData]);

  const badgeMap = useMemo(
    () => scheduleStatusData?.data ?? {},
    [scheduleStatusData],
  );

  const badgeTotal = useMemo(
    () =>
      scheduleStatusData ? sum(Object.values(scheduleStatusData.data)) : 0,
    [scheduleStatusData],
  );

  const previousBadgeTotal = useMemo(() => {
    const prevKey = currentMonth.subtract(1, "month").format("YYYY-MM");
    return Object.entries(badgeMap).reduce(
      (total, [key, count]) =>
        key.startsWith(prevKey) ? total + count : total,
      0,
    );
  }, [badgeMap, currentMonth]);

  const nextBadgeTotal = useMemo(() => {
    const nextKey = currentMonth.add(1, "month").format("YYYY-MM");
    return Object.entries(badgeMap).reduce(
      (total, [key, count]) =>
        key.startsWith(nextKey) ? total + count : total,
      0,
    );
  }, [badgeMap, currentMonth]);

  // Styled badges
  const StyledDayBadge = styled(Badge)(() => ({
    "& .MuiBadge-badge": {
      top: 8,
      height: 15,
      minWidth: 15,
      fontSize: "10px",
      fontWeight: "bold",
    },
  }));

  const StyledTotalBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
      right: -3,
      top: 0,
      border: `2px solid ${(theme.vars ?? theme).palette.background.paper}`,
      padding: "0 4px",
    },
  }));

  // Custom day with badge
  function CustomDay(props) {
    const { day, outsideCurrentMonth, ...other } = props;
    const dateKey = day.format("YYYY-MM-DD");
    const count = badgeMap[dateKey] ?? 0;
    return (
      <StyledDayBadge overlap="circular" badgeContent={count} color="secondary">
        <PickersDay
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          {...other}
        />
      </StyledDayBadge>
    );
  }

  // Custom calendar icon with total badge
  function CustomOpenPickerIcon(props) {
    return (
      <StyledTotalBadge
        overlap="circular"
        badgeContent={badgeTotal}
        color="secondary"
      >
        <CalendarIcon {...props} />
      </StyledTotalBadge>
    );
  }

  // Loading skeleton UI
  if (loadingSchedule) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="text" width="40%" height={40} />
        <Skeleton variant="rectangular" width={200} height={56} />
        {Array.from({ length: 3 }).map((_, idx) => (
          <Box key={idx}>
            <Skeleton variant="text" width="30%" height={30} />
            <Skeleton variant="text" width="20%" />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={20}
              sx={{ mt: 1, mb: 2 }}
            />
            {idx < 2 && <Divider sx={{ my: 2 }} />}
          </Box>
        ))}
      </Stack>
    );
  }

  // Once loaded
  const configs = scheduleData.data.filter(
    ({ program_day }) => program_day.schedule_items_built.length > 0,
  );

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        gap={2}
        justifyContent="space-between"
      >
        <Typography variant="h5" fontWeight="bold">
          {date.toDate().toDateString()} Schedule
        </Typography>
        <EmployeeImpersonationSelect
          value={impersonation}
          onChange={setImpersonation}
          onClear={() => setImpersonation(null)}
          showCaption={true}
        />
      </Stack>

      <MobileDatePicker
        label="Date"
        value={date}
        onChange={(newVal) => {
          if (newVal) {
            setDate(newVal);
            setCurrentMonth(newVal.startOf("month"));
          }
        }}
        closeOnSelect
        onMonthChange={(newMonth) => {
          setCurrentMonth(newMonth.startOf("month"));
        }}
        slots={{
          openPickerIcon: CustomOpenPickerIcon,
          day: CustomDay,
          previousIconButton: (props) => (
            <Badge
              overlap="circular"
              badgeContent={previousBadgeTotal}
              color="secondary"
            >
              <IconButton {...props}>
                <ArrowLeft />
              </IconButton>
            </Badge>
          ),
          nextIconButton: (props) => (
            <Badge
              overlap="circular"
              badgeContent={nextBadgeTotal}
              color="secondary"
            >
              <IconButton {...props}>
                <ArrowRight />
              </IconButton>
            </Badge>
          ),
        }}
        slotProps={{ textField: { variant: "standard", fullWidth: true } }}
      />

      <Box>
        {configs.length ? (
          configs.map(({ program_day, program_week, coverage }, i) => {
            const requiredItemsCount = program_day.schedule_items_built.filter(
              (item) =>
                item.required &&
                item.item_type !== scheduleItemTypes.SECTION_LINK,
            ).length;
            let dayName =
              program_day.order === 0
                ? "Class Day"
                : program_day.order === 6
                  ? "Off Day"
                  : `Day ${program_day.order}`;

            return (
              <Box key={i}>
                <Stack spacing={1}>
                  <Typography variant="body1" fontWeight="bold">
                    {program_week.visible_title || program_week.title} {dayName}{" "}
                    {coverage && (
                      <Chip
                        label={`Covering for ${fullName(
                          coverage.coveree.user,
                        )}`}
                        color="info"
                        size="small"
                      />
                    )}
                  </Typography>
                  <Typography variant="subtitle2" m={0}>
                    ({requiredItemsCount} required item
                    {requiredItemsCount === 1 ? "" : "s"})
                  </Typography>
                  <ScheduleItemList
                    scheduleItems={program_day.schedule_items_built}
                    completedItemsMap={completedItemsMap}
                    date={date}
                    onCompletionStatusChange={() => refetchScheduleStatus()}
                    employeeCoverageId={coverage?.uuid}
                    readOnly={!!impersonatedProfileUuid}
                  />
                </Stack>
                {i < configs.length - 1 && <Divider sx={{ my: 3 }} />}
              </Box>
            );
          })
        ) : (
          <Box
            sx={(theme) => ({
              padding: "1rem",
              backgroundColor: theme.palette.background.default,
              borderRadius: ".5rem",
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            No tasks scheduled for today.
          </Box>
        )}
      </Box>
    </Stack>
  );
}
